import React, { useState } from "react";
import * as finService from "../../services/finService";
import useFinOptions from "./useFinOptions";
import { fmtMoney } from "./gl/glContext";
import { DataTable, act, hint, linkBtn } from "./gl/glUi";
import { AppDropdown, EmptyState, Field, FormDrawer, LoadState, PageHeader, PrimaryButton, SecondaryButton, SlidePanel, formatDate, inputStyle, pageStyle, todayYmd, useLoad } from "../../components/ui";

// Bank Reconciliation: load a statement (paste the bank's CSV or key the lines), match it to the
// bank account's entries in the books (automatically where clear), book charges and interest by
// rule or by hand, and reconcile when every line is matched.

function LoadStatement({ onHide, onDone }) {
  const fin = useFinOptions(["bank-accounts"]);
  const [h, setH] = useState({ bank_account_id: null, statement_number: "", statement_date: todayYmd(), opening_balance: "", closing_balance: "", csv: "" });
  const [busy, setBusy] = useState(false);
  const readFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setH((x) => ({ ...x, csv: String(r.result || "") }));
    r.readAsText(f);
  };
  const save = async () => {
    setBusy(true);
    const res = await act(() => finService.createStatement({ ...h, opening_balance: Number(h.opening_balance || 0), closing_balance: Number(h.closing_balance || 0) }));
    setBusy(false);
    if (res) {
      onDone(res.data.id);
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={() => !busy && onHide()} title="Load a bank statement" width={720} footer={<><SecondaryButton onClick={onHide} disabled={busy}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={busy}>Load</PrimaryButton></>}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0 12px" }}>
        <Field label="Bank account" required>
          <AppDropdown id="bs-bank" value={h.bank_account_id} options={fin["bank-accounts"] || []} onChange={(e) => setH({ ...h, bank_account_id: e.value })} />
        </Field>
        <Field label="Statement number" required>
          <input className="form-control" style={inputStyle} value={h.statement_number} onChange={(e) => setH({ ...h, statement_number: e.target.value })} />
        </Field>
        <Field label="Statement date" required>
          <input type="date" className="form-control" style={inputStyle} value={h.statement_date} onChange={(e) => setH({ ...h, statement_date: e.target.value })} />
        </Field>
        <Field label="Opening balance">
          <input type="number" step="any" className="form-control" style={inputStyle} value={h.opening_balance} onChange={(e) => setH({ ...h, opening_balance: e.target.value })} />
        </Field>
        <Field label="Closing balance">
          <input type="number" step="any" className="form-control" style={inputStyle} value={h.closing_balance} onChange={(e) => setH({ ...h, closing_balance: e.target.value })} />
        </Field>
      </div>
      <Field label="Statement lines (CSV)" hint="Header row with Date, Description, Reference and Amount (or Debit and Credit). Dates as YYYY-MM-DD or DD/MM/YYYY.">
        <input type="file" accept=".csv,text/csv" className="form-control mb-2" onChange={readFile} aria-label="Statement file" />
        <textarea className="form-control" rows={8} value={h.csv} onChange={(e) => setH({ ...h, csv: e.target.value })} placeholder={"Date,Description,Reference,Amount\n2026-10-01,TT FROM CUSTOMER,RCT-2026-000004,1250.00"} />
      </Field>
    </SlidePanel>
  );
}

function MatchPanel({ statement, line, onHide, onDone }) {
  const [chosen, setChosen] = useState([]);
  const items = statement.book_items;
  const sum = items.filter((i) => chosen.includes(i.id)).reduce((s, i) => s + Number(i.amount), 0);
  const toggle = (id) => setChosen(chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id]);
  const save = async () => {
    if (await act(() => finService.statementLineAction(line.id, "match", { journal_line_ids: chosen }))) {
      onDone();
      onHide();
    }
  };
  return (
    <SlidePanel show onHide={onHide} title={`Match: ${line.description}`} width={760} footer={<><SecondaryButton onClick={onHide}>Cancel</SecondaryButton><PrimaryButton onClick={save} disabled={!chosen.length}>Match {fmtMoney(sum)}</PrimaryButton></>}>
      <div style={{ ...hint, marginBottom: 8 }}>
        Statement line {formatDate(line.line_date)}: <b>{fmtMoney(line.amount)}</b>. Choose the entries that make it up.
      </div>
      <DataTable
        rows={items}
        onRowClick={(r) => toggle(r.id)}
        empty={<EmptyState title="Nothing open in the books for this bank" />}
        columns={[
          { label: "", render: (r) => <input type="checkbox" readOnly checked={chosen.includes(r.id)} aria-label={`Choose ${r.je_number}`} /> },
          { label: "Date", render: (r) => formatDate(r.date) },
          { label: "Journal", render: (r) => r.je_number },
          { label: "Description", render: (r) => r.description },
          { label: "Amount", num: true, render: (r) => fmtMoney(r.amount) },
        ]}
      />
    </SlidePanel>
  );
}

function StatementView({ id, onHide, onChanged }) {
  const { data: st, loading, error, reload } = useLoad(() => finService.getStatementRec(id), [id]);
  const [matching, setMatching] = useState(null);
  const [booking, setBooking] = useState(null);
  const refresh = () => {
    reload();
    onChanged();
  };
  const open = st ? st.status !== "RECONCILED" : false;
  return (
    <SlidePanel show onHide={onHide} title={st ? `Statement ${st.statement_number} · ${st.bank_code}` : "Statement"} width={1040}>
      <LoadState loading={loading} error={error} onRetry={reload}>
        {st ? (
          <>
            <div className="d-flex flex-wrap gap-2 mb-3">
              {open ? <PrimaryButton onClick={() => act(() => finService.statementAction(id, "auto")).then(refresh)}>Match automatically</PrimaryButton> : null}
              {open ? <SecondaryButton onClick={() => act(() => finService.statementAction(id, "reconcile")).then(refresh)}>Mark reconciled</SecondaryButton> : null}
              <span style={{ ...hint, alignSelf: "center" }}>
                {formatDate(st.statement_date)} · opening {fmtMoney(st.opening_balance)} · closing {fmtMoney(st.closing_balance)} · {st.status.toLowerCase()}
              </span>
            </div>
            <DataTable
              rows={st.lines}
              columns={[
                { label: "#", render: (l) => l.line_no },
                { label: "Date", render: (l) => formatDate(l.line_date) },
                { label: "Description", render: (l) => `${l.description}${l.reference ? ` (${l.reference})` : ""}` },
                { label: "Amount", num: true, render: (l) => fmtMoney(l.amount) },
                { label: "Matched to", render: (l) => l.matches.map((m) => m.je_number).join(", ") },
                {
                  label: "",
                  render: (l) =>
                    !open ? null : l.status === "UNMATCHED" ? (
                      <span className="d-flex gap-2">
                        <button type="button" style={linkBtn} onClick={() => setMatching(l)}>
                          Match
                        </button>
                        <button type="button" style={linkBtn} onClick={() => setBooking(l)}>
                          Book
                        </button>
                      </span>
                    ) : l.journal_id ? null : (
                      <button type="button" style={linkBtn} onClick={() => act(() => finService.statementLineAction(l.id, "unmatch")).then(refresh)}>
                        Unmatch
                      </button>
                    ),
                },
              ]}
            />
            <div className="mt-3" style={{ maxWidth: 420, marginLeft: "auto", fontSize: 14 }}>
              {[
                ["Balance in the books", st.reconciliation.book_balance],
                ["Less: entries not on a statement yet", -st.reconciliation.not_on_statement],
                ["Adjusted book balance", st.reconciliation.adjusted_book_balance],
                ["Statement closing balance", st.reconciliation.statement_balance],
                ["Difference", st.reconciliation.difference],
              ].map(([k, v]) => (
                <div key={k} className="d-flex justify-content-between" style={{ fontWeight: k === "Difference" ? 700 : 400, color: k === "Difference" && Number(v) ? "#B91C1C" : undefined }}>
                  <span>{k}</span>
                  <span>{fmtMoney(v)}</span>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </LoadState>
      {matching && st ? <MatchPanel statement={st} line={matching} onHide={() => setMatching(null)} onDone={refresh} /> : null}
      <FormDrawer
        show={!!booking}
        title="Book the line"
        initial={booking ? { description: booking.description, account_value: "" } : {}}
        onHide={() => setBooking(null)}
        onDone={refresh}
        onSave={(clean) => finService.statementLineAction(booking.id, "book", clean)}
        fields={[
          { key: "account_value", label: "Account (ACCOUNT value, e.g. 6500 bank charges)", required: true, max: 25 },
          { key: "description", label: "Description", max: 240 },
        ]}
      />
    </SlidePanel>
  );
}

export default function BankReconciliation() {
  const { data, loading, error, reload } = useLoad(() => finService.listStatements(), []);
  const [open, setOpen] = useState(null);
  const [loadingNew, setLoadingNew] = useState(false);
  return (
    <div style={pageStyle}>
      <PageHeader title="Bank Reconciliation" subtitle="Bank statements matched to the books" actions={<PrimaryButton onClick={() => setLoadingNew(true)}>Load statement</PrimaryButton>} />
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={data || []}
          onRowClick={(r) => setOpen(r.id)}
          empty={<EmptyState title="No statements yet" />}
          columns={[
            { label: "Bank", render: (r) => `${r.bank_code} – ${r.bank_name}` },
            { label: "Statement", render: (r) => <b>{r.statement_number}</b> },
            { label: "Date", render: (r) => formatDate(r.statement_date) },
            { label: "Closing", num: true, render: (r) => `${fmtMoney(r.closing_balance)} ${r.currency}` },
            { label: "Unmatched", num: true, render: (r) => Number(r.unmatched) },
            { label: "Status", render: (r) => r.status.toLowerCase() },
          ]}
        />
      </LoadState>
      {open ? <StatementView id={open} onHide={() => setOpen(null)} onChanged={reload} /> : null}
      {loadingNew ? <LoadStatement onHide={() => setLoadingNew(false)} onDone={(id) => {
            reload();
            setOpen(id);
          }} /> : null}
    </div>
  );
}
